'use client';

import { Sun, Moon, MonitorSmartphone } from 'lucide-react';
import { useLocale } from 'next-intl';
import { useTheme } from './theme-provider';
import type { Theme } from '@/lib/theme/constants';
import { cn } from '@/lib/utils';

interface Props {
  variant?: 'header' | 'footer';
  className?: string;
}

const ICONS: Record<Theme, React.ComponentType<{ className?: string }>> = {
  auto: MonitorSmartphone,
  dark: Moon,
  light: Sun,
};

const LABELS_ES = {
  auto: 'Automático (sigue al sistema)',
  dark: 'Modo oscuro',
  light: 'Modo claro',
  cycle: 'Cambiar tema',
};
const LABELS_EN = {
  auto: 'Automatic (follow system)',
  dark: 'Dark mode',
  light: 'Light mode',
  cycle: 'Change theme',
};

/**
 * Toggle de tema NQORO. Cicla auto → light → dark → auto.
 * Icono actual + tooltip con label completo. Estado actual via `aria-label`.
 */
export function ThemeToggle({ variant = 'header', className }: Props) {
  const { theme, setTheme } = useTheme();
  const locale = useLocale();
  const labels = locale === 'en-US' ? LABELS_EN : LABELS_ES;
  const Icon = ICONS[theme];

  const next: Theme = theme === 'auto' ? 'light' : theme === 'light' ? 'dark' : 'auto';

  const handleClick = () => setTheme(next);

  if (variant === 'footer') {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-label={`${labels.cycle} (${labels[theme]})`}
        title={labels[theme]}
        className={cn(
          'type-caption inline-flex items-center gap-1.5 text-[var(--color-fg-subtle)] transition-colors hover:text-[var(--color-accent)]',
          className
        )}
      >
        <Icon className="size-4" />
        <span>{labels[theme]}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={`${labels.cycle} (${labels[theme]})`}
      title={labels[theme]}
      className={cn(
        'inline-flex size-8 items-center justify-center rounded-xs border border-[var(--color-border)] text-[var(--color-fg-muted)] transition-colors hover:border-[var(--color-border-strong)] hover:text-[var(--color-fg)]',
        className
      )}
    >
      <Icon className="size-4" />
    </button>
  );
}
